<template>
  <div class="p-box__body">
    <div class="p-RegularHour__header">
      <div class="p-RegularHour__label">
        <span>{{ displayWeekday(regularHour.wday) }}</span>
      </div>
      <span v-if="showNotSet" class="c-radio__item">
        <input
          :id="`${regularHour.wday}_not_set_checkbox_${index}`"
          :checked="regularHour.isNotSet"
          type="radio"
          class="c-radio__input"
          @change="setNothing(regularHour)"
        >
        <label
          class="c-radio__label"
          :for="`${regularHour.wday}_not_set_checkbox_${index}`"
        >{{ moreHour ? $t('models.moreHour.notSet') : $t('models.storeRegularHour.notSet') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`${regularHour.wday}_close_checkbox_${index}`"
          :checked="!regularHour.noChange && !regularHour.isNotSet && !isOpen"
          type="radio"
          class="c-radio__input"
          @change="removePeriods(regularHour)"
        >
        <label
          class="c-radio__label"
          :for="`${regularHour.wday}_close_checkbox_${index}`"
        >{{ moreHour ? $t('models.moreHour.notSet') : $t('models.storeRegularHour.regularHoliday') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`${regularHour.wday}_open_checkbox_${index}`"
          :checked="!regularHour.noChange && !regularHour.isNotSet && isOpen && !is24h(regularHour)"
          type="radio"
          class="c-radio__input"
          @change="createPeriod(regularHour)"
        >
        <label
          class="c-radio__label"
          :for="`${regularHour.wday}_open_checkbox_${index}`"
        >{{ $t('models.storeRegularHour.businessDay') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`${regularHour.wday}_24h_open_checkbox_${index}`"
          :checked="!regularHour.noChange && !regularHour.isNotSet && isOpen && is24h(regularHour)"
          type="radio"
          class="c-radio__input"
          @change="set24h(regularHour)"
        >
        <label
          class="c-radio__label"
          :for="`${regularHour.wday}_24h_open_checkbox_${index}`"
        >{{ $t('models.storeRegularHour.open24hours') }}</label>
      </span>
    </div>
    <template v-if="isOpen && !is24h(regularHour)">
      <div v-for="(period, k) in regularHour.periods" :key="`${regularHour.wday}_${k}`" class="p-RegularHour__form">
        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.start') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`start_${index}_${k}`"
            v-model="period.openTime"
            :vid="`regularHourStartForm_${index}_${k}`"
            :rules="{
              required: true,
              time_format: true,
              after_pre_time: `@regularHourEndForm_${index}_${k - 1}`,
            }"
            :name="$t('labels.time.start')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.start')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.end') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`end_${index}_${k}`"
            v-model="period.closeTime"
            :vid="`regularHourEndForm_${index}_${k}`"
            :rules="{
              required: true,
              time_format: true,
              clash_with_other_regular_hour: [`@regularHourStartForm_${index}_${k}`, `@regularHourEndForm_${index}_${k}`, regularHour.wday, storeRegularHours]
            }"
            :name="$t('labels.time.end')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.end')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset v-if="availableOption && period.foodOption" class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.lastOrder') }}{{ $t('labels.optionalLabel') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`lastOrderTime_${index}_${k}`"
            v-model="period.foodOption.lastOrderTime"
            :vid="`regularHourLastOrderTimeForm_${index}_${k}`"
            :rules="{
              time_format: true,
              valid_last_order: [`@regularHourStartForm_${index}_${k}`, `@regularHourEndForm_${index}_${k}`]
            }"
            :name="$t('labels.time.lastOrder')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.lastOrder')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset v-if="availableOption && period.foodOption" class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__3 p-formGrid__item__l__3">
          <label class="c-formLabel">{{ $t('labels.time.kind') }}{{ $t('labels.optionalLabel') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`kind_${index}_${k}`"
            v-model="period.foodOption.kind"
            :vid="`regularHourKindForm_${index}_${k}`"
            rules=""
            :name="$t('labels.time.kind')"
          >
            <KkSelect
              :options="timeLabelOptions()"
              :model-value="value"
              @update:model-value="handleChange"
            />
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>

        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__3 p-formGrid__item__l__3">
          <div v-if="regularHour.periods.length > 1" class="p-RegularHour-period-remove">
            <span class="p-RegularHour-period-remove__button" @click="removePeriod(regularHour, k)">
              <img :src="iconCrossPath" alt="" class="p-stores__action__icon">
              {{ $t('labels.delete') }}
            </span>
          </div>
        </fieldset>
      </div>
      <div class="p-RegularHour-period__add c-button c-button--icon">
        <div class="c-button__item c-button__item--primary" @click="addPeriod(regularHour)">
          <img :src="ImgIconPlusRed" alt="" class="c-button__icon">
          <span>{{ $t('labels.add', { value: $t('models.storeRegularHour.modelName') }) }}（{{ $t('models.storeRegularHour.caseOfMultiHours') }}）</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ImgIconPlusRed from '../../../images/common/icon_plus_red.svg'
import iconCrossPath from '../../../images/common/icon_cross.svg'
import ValidationError from './ValidationError'
import { KkSelect } from '../components/base'
import { mapState } from 'vuex'

export default {
  name: 'RegularHourCard',
  components: {
    ValidationError,
    KkSelect
  },
  props: {
    regularHour: {
      type: Object,
      required: true,
    },
    hourType: {
      type: String,
      required: true,
      validator: (value) => {
        return ['regular', 'holiday', 'dayBeforeHoliday'].indexOf(value) !== -1
      }
    },
    index: {
      type: String,
      required: true,
    },
    moreHour: {
      type: Boolean,
      default: false
    },
    atBulk: {
      type: Boolean,
      default: false
    },
    availableOption : {
      type: Boolean,
      default: false
    },
  },
  emits: ['set-field-value'],
  computed: {
    ...mapState({
      storeRegularHours: state => state.companiesStoreBusinessHours.regularHours,
    }),
    ImgIconPlusRed: () => ImgIconPlusRed,
    iconCrossPath: () => iconCrossPath,
    isOpen() {
      return this.regularHour.periods.length !== 0
    },
    showNotSet() {
      return this.hourType != 'regular' && !this.atBulk
    }
  },
  created() {
    for (let period of this.regularHour.periods) {
      if (!period.foodOption) this.setFoodOptions(period)
    }
  },
  methods: {
    is24h(regularHour) {
      if (regularHour.periods.length >= 2) return false // 24時間営業の時はperiods.lengthは1になるため、それ以上あればfalse

      const period = regularHour.periods[0]
      return period?.openTime === '00:00' && period?.closeTime === '24:00'
    },
    set24h(val) {
      val.noChange = false
      val.isNotSet = false
      val.periods = [{openTime: '00:00', closeTime: '24:00'}]
    },
    createPeriod(val) {
      this.resetFormValues(0)
      val.noChange = false
      val.isNotSet = false
      if (this.availableOption) {
        val.periods = [{
          openTime: '',
          closeTime: '',
          foodOption: {
            lastOrderTime: '',
            kind: ''
          }
        }]
      } else {
        val.periods = [{
          openTime: '',
          closeTime: ''
        }]
      }
    },
    removePeriods(val) {
      val.noChange = false
      val.isNotSet = false
      val.periods = []
    },
    setNothing(val) {
      val.isNotSet = true
      val.periods = []
    },
    addPeriod(val) {
      this.resetFormValues(val.periods.length)
      if (this.availableOption) {
        val.periods.push({openTime: '', closeTime: '', foodOption: { lastOrderTime: '', kind: '' }})
      } else {
        val.periods.push({openTime: '', closeTime: ''})
      }
    },
    removePeriod(val, key) {
      val.periods.splice(key, 1)
    },
    displayWeekday(weekday) {
      switch (this.hourType) {
      case 'regular':
        return this.$t('labels.weekday.' + weekday)
      case 'holiday':
        return this.$t('labels.holiday')
      case 'dayBeforeHoliday':
        return this.$t('labels.dayBeforeHoliday')
      }
    },
    timeLabelOptions() {
      let timeLabelOptions = {}
      timeLabelOptions[this.$t('labels.notSet')] = ''
      const timeLabelList = ['morning', 'lunch', 'dinner']
      for (let timeLabel of timeLabelList) {
        timeLabelOptions[this.$t(`pages.CompaniesLocationStore.businessHours.regularHours.timeLabel.${timeLabel}`)] = timeLabel
      }
      return timeLabelOptions
    },
    setFoodOptions(period) {
      period.foodOption = { lastOrderTime: '', kind: '' }
    },
    resetFormValues(num) {
      // ValidationProviderのvalueの値が残ってしまうことがあるので、時間を追加するたびにresetするようにする
      this.$emit('set-field-value', `regularHourStartForm_${this.index}_${num}`, '')
      this.$emit('set-field-value', `regularHourEndForm_${this.index}_${num}`, '')
      this.$emit('set-field-value', `regularHourLastOrderTimeForm_${this.index}_${num}`, '')
      this.$emit('set-field-value', `regularHourKindForm_${this.index}_${num}`, '')
    }
  }
}
</script>

<style lang="scss" scoped>
.p-RegularHour {
  &__header {
    vertical-align: baseline;
  }
  &__label {
    padding: 0 0 12px;
    font-weight: bold;
    font-size: 18px;
    display: inline-block;
    width: 70px;
  }
  &__form {
    display: inline-flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 24px -8px 0;
  }
  &-period {
    &__form {
      padding: 0 8px 8px;
    }
    &-remove {
      padding: 32px 0 12px 0;
      &__button {
        cursor: pointer;
        display: inline-flex;
        flex-wrap: wrap;
      }
    }
    &__add {
      margin-top: 0;
    }
  }
}

</style>
